import { immediateToast } from "izitoast-react";
import { EMPTY_STRING } from "src/components/constant/constant";
import { ListAsset } from "src/types/commonTypes";
import {
  AssetObjectType,
  AssetUrl,
  UploadAssetType,
  getAssetUrls,
  prepareAssetUpload
} from "src/network/graphql/assetService";
import { downloadPresignedUrl, openPresignedUrl, parsePresignedFields, postFileToPresignedUrl } from "./presignedUtils";
import { getErrorPayloadCode, isUnauthorizedError } from "./errors";

export const ASSET_MAX_MB = 10;
const ASSET_MAX_BYTES = ASSET_MAX_MB * 1024 * 1024;
const ASSET_URL_BATCH_SIZE = 50;

export type AssetSource = { object_type: AssetObjectType; object_id: string };

// A listed asset plus the object it was listed under; get_asset_urls is authorised per object.
export type SourcedAsset = ListAsset & { source: AssetSource };

export type AssetLinks = { urls: Record<string, string>; expiresAt: number };

export const isPdfFile = (fileName?: string): boolean => {
  return fileName?.toLowerCase().endsWith(".pdf") ?? false;
};

// No browser draws TIFF reliably, so these get a file tile and no view link.
const NON_PREVIEWABLE_EXTENSIONS = ["tif", "tiff"];
// Safari draws HEIC/HEIF, other browsers don't: try once, and on error show the tile (the link is fine).
const BROWSER_DEPENDENT_EXTENSIONS = ["heic", "heif"];

export const getFileName = (key: string): string => key.split("/").pop() || key;

export const getFileExtension = (fileName?: string): string => {
  const name = fileName ? getFileName(fileName) : EMPTY_STRING;
  const dotIndex = name.lastIndexOf(".");

  return dotIndex >= 0 ? name.slice(dotIndex + 1).toLowerCase() : EMPTY_STRING;
};

export const isNonPreviewableFile = (fileName?: string): boolean =>
  NON_PREVIEWABLE_EXTENSIONS.includes(getFileExtension(fileName));

export const isBrowserDependentImage = (fileName?: string): boolean =>
  BROWSER_DEPENDENT_EXTENSIONS.includes(getFileExtension(fileName));

export const isPreviewableImage = (fileName?: string): boolean =>
  !isPdfFile(fileName) && !isNonPreviewableFile(fileName);

const isAllowedAssetType = (contentType: string): boolean => {
  const type = contentType.toLowerCase();

  if (type === "application/pdf") return true;
  return type.startsWith("image/") && type !== "image/svg+xml";
};

// Mirrors prepare_asset_upload's limits so the user hears about a bad file before anything is sent.
export const getAssetFileError = (file: File): string => {
  if (!isAllowedAssetType(file.type)) return `"${file.name}" isn't supported. Upload an image or a PDF.`;
  if (file.size === 0) return `"${file.name}" is empty.`;
  if (file.size > ASSET_MAX_BYTES) return `"${file.name}" is larger than ${ASSET_MAX_MB} MB.`;
  return EMPTY_STRING;
};

export const getAssetUploadErrorMessage = (error: unknown): string => {
  if (isUnauthorizedError(error)) return "You don't have permission to upload files here.";
  if (getErrorPayloadCode(error) === "VALIDATION_ERROR") {
    return `This file can't be uploaded. Use an image or a PDF up to ${ASSET_MAX_MB} MB.`;
  }
  return "Upload failed. Please try again.";
};

export const getAssetLinkErrorMessage = (error: unknown): string => {
  if (isUnauthorizedError(error)) return "You don't have access to these files.";
  return "Files couldn't be loaded. Please try again.";
};

type UploadAssetArgs = AssetSource & { asset_type: UploadAssetType; file: File };

// The Asset row is created asynchronously by the S3 event, so callers refetch after this resolves.
export const uploadAsset = async ({ file, object_type, object_id, asset_type }: UploadAssetArgs): Promise<void> => {
  const response = await prepareAssetUpload({
    object_type,
    object_id,
    asset_type,
    file_name: file.name,
    content_type: file.type,
    file_size: file.size
  });
  const prepared = response.data?.prepare_asset_upload;

  if (!prepared?.url) throw new Error("Upload could not be prepared");
  await postFileToPresignedUrl({ url: prepared.url, fields: parsePresignedFields(prepared.fields), file });
};

const groupBySource = (assets: SourcedAsset[]): { source: AssetSource; assetIds: string[] }[] => {
  const groups = new Map<string, { source: AssetSource; assetIds: string[] }>();

  assets.forEach((asset) => {
    const groupKey = `${asset.source.object_type}:${asset.source.object_id}`;
    const group = groups.get(groupKey);

    if (group) {
      group.assetIds.push(asset.asset_id);
      return;
    }
    groups.set(groupKey, { source: asset.source, assetIds: [asset.asset_id] });
  });

  return Array.from(groups.values());
};

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];

  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

// Fresh short-lived links keyed by asset_id; fetch right before use and never hold past expiresAt.
export const fetchAssetUrls = async (assets: SourcedAsset[], download = false): Promise<AssetLinks> => {
  const requests = groupBySource(assets).flatMap(({ source, assetIds }) =>
    chunk(assetIds, ASSET_URL_BATCH_SIZE).map((batch) =>
      getAssetUrls({ ...source, asset_ids: batch, ...(download && { download }) })
    )
  );
  const responses = await Promise.all(requests);
  const links: AssetUrl[] = responses.flatMap((response) => response.data?.get_asset_urls ?? []);
  const urls: Record<string, string> = {};
  let expiresAt = Number.POSITIVE_INFINITY;

  links.forEach((link) => {
    if (!link?.url) return;
    urls[link.asset_id] = link.url;
    expiresAt = Math.min(expiresAt, Date.parse(link.expires_at));
  });

  return { urls, expiresAt };
};

export const fetchAssetUrl = async (asset: SourcedAsset, download = false): Promise<string> => {
  const { urls } = await fetchAssetUrls([asset], download);
  const url = urls[asset.asset_id];

  if (!url) throw new Error("File link unavailable");
  return url;
};

const toastLinkError = (error: unknown) => {
  immediateToast("error", { message: getAssetLinkErrorMessage(error), timeout: 3000, position: "topCenter" });
};

export const downloadAsset = async (asset: SourcedAsset): Promise<void> => {
  try {
    downloadPresignedUrl(await fetchAssetUrl(asset, true));
  } catch (error) {
    toastLinkError(error);
  }
};

export const openAsset = async (asset: SourcedAsset): Promise<void> => {
  try {
    openPresignedUrl(await fetchAssetUrl(asset));
  } catch (error) {
    toastLinkError(error);
  }
};
